'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  type FinalizingSession,
  type PaymentRedirectContext,
  type PaymentRedirectResolution,
  resolvePaymentRedirectFlow,
} from '../lib/paymentRedirectFlow';

// ─── Constants ───

const SETTLEMENT_POLL_INTERVAL_MS = 3_000;
const SETTLEMENT_POLL_TIMEOUT_MS = 60_000;
const FINALIZATION_TIMEOUT_MS = 30_000;

// ─── Input / Output types ───

export interface UsePaymentRedirectResolutionInput {
  /** Whether this mount is a redirect return */
  isRedirectReturn: boolean;
  /** Donation ID from redirect return URL */
  donationId: string | null;
  /** Adyen redirectResult from URL */
  redirectResult: string | null;
  /** Adyen sessionId from URL */
  returnSessionId: string | null;
  /** Redirect context query result (from getRedirectContext) */
  redirectContext: PaymentRedirectContext | null;
  /** Whether redirect context query errored */
  isRedirectContextError: boolean;
  /** Stored session data from sessionStorage (already loaded by consumer) */
  storedSessionData: string | null;
  /** Whether session recovery has been attempted (for IAB flows) */
  recoveryAttempted: boolean;
}

export interface SettlementPollingState {
  isPolling: boolean;
  pollCount: number;
  startedAt: number;
  timedOut: boolean;
}

export interface UsePaymentRedirectResolutionResult {
  /** The resolved redirect flow — null until resolution runs */
  resolution: PaymentRedirectResolution | null;
  /** Session data for finalize-payment path — set once as re-entry guard */
  redirectSession: FinalizingSession | null;
  /** Settlement polling state */
  settlement: SettlementPollingState;
  /** Whether SDK finalization has timed out (30s) */
  finalizingTimedOut: boolean;
  /** The refetchInterval value to pass to getRedirectContext query */
  pollInterval: number | false;
  /** Dismiss the finalization timeout warning */
  dismissFinalizingTimeout: () => void;
}

// ─── Hook ───

export function usePaymentRedirectResolution(
  input: UsePaymentRedirectResolutionInput,
): UsePaymentRedirectResolutionResult {
  const {
    isRedirectReturn,
    donationId,
    redirectResult,
    returnSessionId,
    redirectContext,
    isRedirectContextError,
    storedSessionData,
    recoveryAttempted,
  } = input;

  // ── State ──

  const [resolution, setResolution] = useState<PaymentRedirectResolution | null>(null);
  const [redirectSession, setRedirectSession] = useState<FinalizingSession | null>(null);
  const [finalizingTimedOut, setFinalizingTimedOut] = useState(false);

  // ── Settlement polling state ──

  const [settlementPolling, setSettlementPolling] = useState(false);
  const [settlementStartedAt, setSettlementStartedAt] = useState(0);
  const [settlementPollCount, setSettlementPollCount] = useState(0);
  const [settlementTimedOut, setSettlementTimedOut] = useState(false);

  // ── Core resolution effect ──

  useEffect(() => {
    if (!isRedirectReturn || !donationId || !redirectResult || !redirectContext) {
      return;
    }

    // Wait for session recovery before resolving
    if (!recoveryAttempted) return;

    // State guard: once redirectSession is set (finalize-payment), don't re-resolve
    // (unless we're polling — in that case redirectSession is still null)
    if (redirectSession) return;

    const result = resolvePaymentRedirectFlow({
      donationId,
      redirectResult,
      sessionId: returnSessionId,
      redirectContext,
      storedSessionData,
    });

    queueMicrotask(() => {
      setResolution(result);

      switch (result.kind) {
        case 'finalize-payment':
          setRedirectSession(result.session);
          break;

        case 'awaiting-settlement':
          setSettlementPolling((prev) => {
            if (!prev) {
              setSettlementStartedAt(Date.now());
              setSettlementPollCount(0);
            } else {
              setSettlementPollCount((c) => c + 1);
            }
            return true;
          });
          break;

        case 'already-settled':
        case 'failed':
          // Stop polling if it was active
          setSettlementPolling(false);
          break;
      }
    });
  }, [
    isRedirectReturn,
    donationId,
    redirectResult,
    redirectContext,
    recoveryAttempted,
    redirectSession,
    returnSessionId,
    storedSessionData,
  ]);

  // ── Error effect — treat context load failure as failed ──

  useEffect(() => {
    if (!isRedirectReturn || !isRedirectContextError) return;
    queueMicrotask(() => {
      setResolution({ kind: 'failed', status: 'error' });
    });
  }, [isRedirectReturn, isRedirectContextError]);

  // ── Settlement polling timeout (60s) ──

  useEffect(() => {
    if (!settlementPolling || !settlementStartedAt) return;

    const elapsed = Date.now() - settlementStartedAt;
    if (elapsed >= SETTLEMENT_POLL_TIMEOUT_MS) {
      queueMicrotask(() => setSettlementTimedOut(true));
    } else {
      const timer = setTimeout(() => {
        setSettlementTimedOut(true);
      }, SETTLEMENT_POLL_TIMEOUT_MS - elapsed);
      return () => clearTimeout(timer);
    }
  }, [settlementPolling, settlementStartedAt]);

  // ── SDK finalization timeout (30s) ──

  useEffect(() => {
    if (!redirectSession) {
      queueMicrotask(() => setFinalizingTimedOut(false));
      return;
    }
    const timer = setTimeout(() => {
      setFinalizingTimedOut(true);
    }, FINALIZATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [redirectSession]);

  // ── Callbacks ──

  const dismissFinalizingTimeout = useCallback(() => {
    setFinalizingTimedOut(false);
  }, []);

  // ── Derived state ──

  const isPolling = settlementPolling && resolution?.kind === 'awaiting-settlement';
  const pollInterval = isPolling ? SETTLEMENT_POLL_INTERVAL_MS : false;

  return {
    resolution,
    redirectSession,
    settlement: {
      isPolling,
      pollCount: settlementPollCount,
      startedAt: settlementStartedAt,
      timedOut: settlementTimedOut,
    },
    finalizingTimedOut,
    pollInterval,
    dismissFinalizingTimeout,
  };
}
